// ─── Helpers ───
import { V } from './theme';
import { LS } from './storage';

// iOS detection that also catches iPadOS (which reports a Macintosh user agent)
// Onboarding "current lifts" seed, converted to the display units the rest of the app uses.
// Stored as {bench,squat,deadlift,units} at signup; the user may switch units later.
export function liftsIn(lifts, units) {
  if (!lifts || typeof lifts !== "object") return null;
  const f = lifts.units === units ? 1 : lifts.units === "kg" ? 2.20462 : units === "kg" ? 0.453592 : 1;
  const out = { bench: Math.round((parseFloat(lifts.bench) || 0) * f), squat: Math.round((parseFloat(lifts.squat) || 0) * f), deadlift: Math.round((parseFloat(lifts.deadlift) || 0) * f) };
  return out.bench || out.squat || out.deadlift ? out : null;
}

export const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1);
};
// True when running as an installed home-screen / standalone app
export const isStandalone = () => {
  if (typeof window === "undefined") return false;
  if (window.navigator.standalone === true) return true;
  try { return !!(window.matchMedia && window.matchMedia("(display-mode: standalone)").matches); } catch { return false; }
};

export const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : (Date.now().toString(36) + Math.random().toString(36).slice(2,11));

// Local-time YYYY-MM-DD (NOT UTC) so "today" reflects the user's wall clock
const _pad = (n) => String(n).padStart(2,"0");
const _ymdLocal = (d) => `${d.getFullYear()}-${_pad(d.getMonth()+1)}-${_pad(d.getDate())}`;
// Local-time YYYY-MM-DD for an arbitrary Date — use instead of toISOString().slice(0,10),
// which yields a UTC date that disagrees with locally-stored `date` fields west of UTC.
export const ymdLocal = (d) => _ymdLocal(d);
export const today = () => _ymdLocal(new Date());
export const fmtShort = (d) => new Date(d+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"});
export const fmtFull = (d) => new Date(d+"T12:00:00").toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"});
export const ago = (n) => { const d=new Date(); d.setDate(d.getDate()-n); return _ymdLocal(d); };

// Shared streak calculation — counts consecutive days with workouts ending at today (or yesterday if no workout today)
export const calcStreak = (workouts) => {
  const w=workouts||[]; if(!w.length) return 0;
  const dates=new Set(w.map(x=>x.date));
  // Days bridged by a consumed streak shield count as trained
  try{(LS.get("ft-shield-covered-dates")||[]).forEach(d=>dates.add(d));}catch{}
  let start=0;
  if(!dates.has(today()))start=1; // grace for "haven't logged today yet"
  let c=0;
  for(let i=start;i<365;i++){
    if(dates.has(ago(i)))c++; else break;
  }
  return c;
};
// Safely display a friend's name — never shows raw email
export const friendDisplayName=(f)=>{
  const n=f?.name||"";
  if(!n||n.includes("@")||n.trim()==="null null"||n.trim()==="undefined undefined"){
    const e=f?.email||"";
    if(!e)return"Unknown";
    const prefix=e.split("@")[0].replace(/[._\-+]/g," ");
    return prefix.replace(/\b\w/g,c=>c.toUpperCase()).trim()||"Unknown";
  }
  return n.trim()||"Unknown";
};
export const calc1RM = (w,r) => {
  const W=+w, R=+r;
  if(!Number.isFinite(W)||!Number.isFinite(R)||W<=0||R<1)return 0;
  return R===1?Math.round(W):Math.round(W*(1+R/30));
};
export const fmtTimer = (s) => `${Math.floor(s/60)}:${(s%60).toString().padStart(2,'0')}`;
export const PLATES=[45,35,25,10,5,2.5];
export const PLATES_KG=[20,15,10,5,2.5,1.25];
// Bar weight defaults to 45 lb / 20 kg. Pass `units` to switch.
export const calcPlates=(total,units="lbs")=>{
  const bar=units==="kg"?20:45;
  const plates=units==="kg"?PLATES_KG:PLATES;
  const T=+total; if(!Number.isFinite(T))return null;
  let ps=(T-bar)/2;
  if(ps<=0)return T<=bar?[]:null;
  // Tolerance for float math
  const r=[];
  for(const p of plates){while(ps+1e-9>=p){r.push(p);ps-=p;}}
  return Math.abs(ps)<1e-6?r:null;
};
// Precise lb↔kg constant
const LB_PER_KG=2.2046226218487757;
export const toKg=(lbs)=>{const v=+lbs;return Number.isFinite(v)?Math.round(v/LB_PER_KG*10)/10:0;};
export const toLbs=(kg)=>{const v=+kg;return Number.isFinite(v)?Math.round(v*LB_PER_KG*10)/10:0;};
export const wUnit=(units)=>units==="kg"?"kg":"lbs";
export const dUnit=(units)=>units==="kg"?"km":"mi";
export const isCardio=(exerciseId,exercises)=>exercises.find(e=>e.id===exerciseId)?.cat==="Cardio";
// convW: convert a value stored in lbs to the user's display unit.
// (kept for back-compat — callers pass lb-stored values and want display values)
export const convW=(v,units)=>units==="kg"?toKg(v):(+v||0);

// Shared recharts config — getters resolve at render time so charts follow the active theme
export const chartCfg={
  get grid(){return{strokeDasharray:"3 3",stroke:V.mode==="dark"?"rgba(255,255,255,0.06)":"rgba(15,23,42,0.08)"};},
  get axis(){return{tick:{fill:V.mode==="dark"?"rgba(255,255,255,0.4)":"rgba(15,23,42,0.45)",fontSize:10,fontFamily:V.font},axisLine:false,tickLine:false};},
  get tip(){return{
    contentStyle:{background:V.mode==="dark"?"rgba(24,24,36,0.96)":"rgba(255,255,255,0.98)",
      border:`1px solid ${V.cardBorder}`,borderRadius:10,fontSize:12,color:V.text,
      boxShadow:V.shadowLg,fontFamily:V.font},
    itemStyle:{color:V.text},
    labelStyle:{color:V.text2,fontWeight:600},
    cursor:{stroke:V.mode==="dark"?"rgba(255,255,255,0.15)":"rgba(15,23,42,0.2)",strokeWidth:1},
  };},
};
